/**
 * Mock Data Service Module
 *
 * Provides static fleet performance data matching the expected backend API
 * response structure (FleetPerformanceResponse). This module will be replaced
 * by the actual backend API integration when it is ready.
 *
 * Requirements: 4.1, 4.2, 4.5
 */

import { getRiskRatingLabel } from "./riskRatingConfig";

/**
 * Top 5 vehicles sorted by total_point descending (highest first).
 * Vehicles with high fuel economy, low idling, low CO₂, and low risk rating
 * score the most points.
 */
const top_vehicles = [
  {
    plate_number: "B 1234 ABC",
    vehicle_type: "Sedan",
    fuel_economy_value: 14.52,
    fuel_economy_point: 95,
    idling_percentage: 8.3,
    idling_point: 92,
    co2_emission_value: 1.245,
    co2_emission_point: 90,
    risk_rating_label: getRiskRatingLabel(8.3),
    risk_rating_point: 93,
    total_point: 370,
  },
  {
    plate_number: "B 2345 DEF",
    vehicle_type: "SUV",
    fuel_economy_value: 12.80,
    fuel_economy_point: 88,
    idling_percentage: 12.5,
    idling_point: 85,
    co2_emission_value: 1.890,
    co2_emission_point: 82,
    risk_rating_label: getRiskRatingLabel(12.5),
    risk_rating_point: 88,
    total_point: 343,
  },
  {
    plate_number: "B 3456 GHI",
    vehicle_type: "Pickup",
    fuel_economy_value: 11.35,
    fuel_economy_point: 82,
    idling_percentage: 15.0,
    idling_point: 80,
    co2_emission_value: 2.105,
    co2_emission_point: 78,
    risk_rating_label: getRiskRatingLabel(15.0),
    risk_rating_point: 85,
    total_point: 325,
  },
  {
    plate_number: "B 4567 JKL",
    vehicle_type: "Van",
    fuel_economy_value: 10.90,
    fuel_economy_point: 78,
    idling_percentage: 18.2,
    idling_point: 76,
    co2_emission_value: 2.340,
    co2_emission_point: 75,
    risk_rating_label: getRiskRatingLabel(18.2),
    risk_rating_point: 80,
    total_point: 309,
  },
  {
    plate_number: "B 5678 MNO",
    vehicle_type: "Sedan",
    fuel_economy_value: 10.25,
    fuel_economy_point: 75,
    idling_percentage: 19.8,
    idling_point: 73,
    co2_emission_value: 2.510,
    co2_emission_point: 72,
    risk_rating_label: getRiskRatingLabel(19.8),
    risk_rating_point: 78,
    total_point: 298,
  },
];

/**
 * Worst 5 vehicles sorted by total_point ascending (lowest first).
 * Vehicles with poor fuel economy, high idling, high CO₂, and high risk rating
 * score the fewest points.
 */
const worst_vehicles = [
  {
    plate_number: "B 9012 XYZ",
    vehicle_type: "Truck",
    fuel_economy_value: 4.15,
    fuel_economy_point: 25,
    idling_percentage: 72.4,
    idling_point: 20,
    co2_emission_value: 8.920,
    co2_emission_point: 18,
    risk_rating_label: getRiskRatingLabel(72.4),
    risk_rating_point: 15,
    total_point: 78,
  },
  {
    plate_number: "B 8901 WVU",
    vehicle_type: "Truck",
    fuel_economy_value: 4.80,
    fuel_economy_point: 30,
    idling_percentage: 65.0,
    idling_point: 28,
    co2_emission_value: 7.650,
    co2_emission_point: 25,
    risk_rating_label: getRiskRatingLabel(65.0),
    risk_rating_point: 22,
    total_point: 105,
  },
  {
    plate_number: "B 7890 TSR",
    vehicle_type: "SUV",
    fuel_economy_value: 5.60,
    fuel_economy_point: 38,
    idling_percentage: 55.3,
    idling_point: 35,
    co2_emission_value: 6.430,
    co2_emission_point: 32,
    risk_rating_label: getRiskRatingLabel(55.3),
    risk_rating_point: 30,
    total_point: 135,
  },
  {
    plate_number: "B 6789 QPO",
    vehicle_type: "Van",
    fuel_economy_value: 6.45,
    fuel_economy_point: 42,
    idling_percentage: 48.7,
    idling_point: 40,
    co2_emission_value: 5.280,
    co2_emission_point: 38,
    risk_rating_label: getRiskRatingLabel(48.7),
    risk_rating_point: 35,
    total_point: 155,
  },
  {
    plate_number: "B 5670 NML",
    vehicle_type: "Pickup",
    fuel_economy_value: 7.20,
    fuel_economy_point: 48,
    idling_percentage: 42.1,
    idling_point: 45,
    co2_emission_value: 4.750,
    co2_emission_point: 42,
    risk_rating_label: getRiskRatingLabel(42.1),
    risk_rating_point: 40,
    total_point: 175,
  },
];

/**
 * Static mock data matching the FleetPerformanceResponse interface.
 * - top_vehicles: sorted by total_point descending (highest first)
 * - worst_vehicles: sorted by total_point ascending (lowest first)
 */
export const fleetPerformanceMockData = {
  top_vehicles,
  worst_vehicles,
};
